import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface RcaRecord {
  id: string
  conversationId: string
  categoryId: string | null
  subcategoryId: string | null
  probableCause: string
  probableFailureMode: string
  why1: string | null
  why1Check: string | null
  why2: string | null
  why2Check: string | null
  why3: string | null
  why3Check: string | null
  why4: string | null
  why4Check: string | null
  why5: string | null
  why5Check: string | null
  correctiveAction: string | null
  preventiveAction: string | null
  fourM: string | null
  ownerFpr: string | null
  timeline: string | null
  createdBy: string | null
  modifiedBy: string | null
  createdAt: string
  updatedAt: string
}

interface RcaListResponse {
  records: RcaRecord[]
}

interface RcaRecordResponse {
  record: RcaRecord
}

export interface RcaTemplate {
  id: string
  probableCause: string
  probableFailureMode: string
}

interface RcaTemplatesResponse {
  templates: RcaTemplate[]
}

export interface RcaRecordInput {
  probableCause: string
  probableFailureMode: string
  categoryId?: string | null
  subcategoryId?: string | null
  why1?: string | null
  why1Check?: string | null
  why2?: string | null
  why2Check?: string | null
  why3?: string | null
  why3Check?: string | null
  why4?: string | null
  why4Check?: string | null
  why5?: string | null
  why5Check?: string | null
  correctiveAction?: string | null
  preventiveAction?: string | null
  fourM?: string | null
  ownerFpr?: string | null
  timeline?: string | null
}

export function useConversationRcaTab(conversationId: string | null) {
  const [records, setRecords] = useState<RcaRecord[]>([])
  const [templates, setTemplates] = useState<RcaTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRecords = useCallback(async () => {
    if (!conversationId) return
    setLoading(true)
    setError(null)
    try {
      const data = await api.get<RcaListResponse>(`/conversations/${conversationId}/rca`)
      setRecords(data.records ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load RCA records')
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  const fetchTemplates = useCallback(
    async (categoryId: string | null, subcategoryId: string | null) => {
      if (!conversationId || !categoryId || !subcategoryId) {
        setTemplates([])
        return
      }
      try {
        const data = await api.get<RcaTemplatesResponse>(
          `/conversations/${conversationId}/rca/templates?categoryId=${categoryId}&subcategoryId=${subcategoryId}`,
        )
        setTemplates(data.templates ?? [])
      } catch {
        setTemplates([])
      }
    },
    [conversationId],
  )

  useEffect(() => {
    void fetchRecords()
  }, [fetchRecords])

  const createRecord = useCallback(
    async (input: RcaRecordInput): Promise<RcaRecord | null> => {
      if (!conversationId) return null
      setSaving(true)
      setError(null)
      try {
        const data = await api.post<RcaRecordResponse>(`/conversations/${conversationId}/rca`, input)
        setRecords((prev) => [...prev, data.record])
        return data.record
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create RCA record')
        return null
      } finally {
        setSaving(false)
      }
    },
    [conversationId],
  )

  const updateRecord = useCallback(
    async (recordId: string, input: Partial<RcaRecordInput>): Promise<boolean> => {
      if (!conversationId) return false
      setSaving(true)
      setError(null)
      try {
        const data = await api.patch<RcaRecordResponse>(
          `/conversations/${conversationId}/rca/${recordId}`,
          input,
        )
        setRecords((prev) => prev.map((r) => (r.id === recordId ? data.record : r)))
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update RCA record')
        return false
      } finally {
        setSaving(false)
      }
    },
    [conversationId],
  )

  const deleteRecord = useCallback(
    async (recordId: string): Promise<boolean> => {
      if (!conversationId) return false
      setSaving(true)
      setError(null)
      try {
        await api.delete(`/conversations/${conversationId}/rca/${recordId}`)
        setRecords((prev) => prev.filter((r) => r.id !== recordId))
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete RCA record')
        return false
      } finally {
        setSaving(false)
      }
    },
    [conversationId],
  )

  return {
    records,
    templates,
    loading,
    saving,
    error,
    createRecord,
    updateRecord,
    deleteRecord,
    fetchTemplates,
    refresh: fetchRecords,
  }
}
